import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage, LanguageToggle } from '../context/LanguageContext';
import { Lock, Mail, Eye, EyeOff, Shield, MessageSquare, Zap, ArrowRight, AlertCircle, UserPlus } from 'lucide-react';
import OrbitLogo from '../assets/OrbitLogo.png';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';

/* ── Header ── */
function Header() {
  return (
    <header className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-5 md:px-6 lg:px-10 py-4 md:py-5">
      <img src={OrbitLogo} alt="Capital Infusion Orbit" className="h-16 lg:h-32 w-auto object-contain" />
      <div className="flex items-center gap-4">
        <LanguageToggle />
        <div className="hidden sm:flex items-center gap-2.5">
          <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
            <Lock size={14} className="text-green-600" />
          </div>
          <div>
            <p className="text-gray-700 text-xs font-semibold leading-tight">100% Secure</p>
            <p className="text-gray-400 text-xs leading-tight">Bank-level encryption</p>
          </div>
        </div>
      </div>
    </header>
  );
}

/* ── Feature highlights ── */
function FeatureHighlights() {
  const { t } = useLanguage();
  const items = [
    { icon: Shield, title: t('Secure & Encrypted'), desc: t('Bank-level encryption') },
    { icon: MessageSquare, title: t('Direct Communication'), desc: t('Message your advisor') },
    { icon: Zap, title: t('Faster Reviews'), desc: t('Documents reviewed quickly') },
  ];
  return (
    <div className="hidden md:grid grid-cols-3 gap-4 mt-10">
      {items.map(f => (
        <div
          key={f.title}
          className="bg-white/[0.07] backdrop-blur-md border border-white/10 rounded-xl p-4 transition-all duration-200 hover:bg-white/[0.11] hover:border-white/20"
          style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)' }}
        >
          <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center mb-3 border border-white/10">
            <f.icon size={16} className="text-blue-300" />
          </div>
          <p className="text-white text-sm font-semibold tracking-tight">{f.title}</p>
          <p className="text-blue-200/60 text-xs mt-0.5">{f.desc}</p>
        </div>
      ))}
    </div>
  );
}

/* ── Login card ── */
function LoginCard() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [regForm, setRegForm] = useState({ full_name: '', business_name: '', email: '', password: '', confirm: '' });
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    if (regForm.password !== regForm.confirm) return setRegError('Passwords do not match');
    if (regForm.password.length < 8) return setRegError('Password must be at least 8 characters');
    setRegLoading(true);
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: regForm.email, full_name: regForm.full_name, business_name: regForm.business_name, password: regForm.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Registration failed');
      await login(regForm.email, regForm.password);
      navigate('/');
    } catch (err) {
      setRegError(err.message);
    } finally {
      setRegLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Please enter both email and password.'); return; }
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await fetch(`${API}/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
    } catch {}
    setForgotMsg('If an account exists with that email, a reset link has been sent.');
    setForgotLoading(false);
  };

  if (forgotMode) {
    return (
      <div className="bg-white rounded-2xl p-6 md:p-8 w-full max-w-md" style={{ border: '1px solid var(--line)', boxShadow: 'var(--shadow-xl)' }}>
        <div className="text-center mb-6">
          <div className="w-11 h-11 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Mail size={20} className="text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
          <p className="text-gray-400 text-sm mt-1">Enter your email and we'll send a reset link</p>
        </div>
        {forgotMsg && (
          <div className="bg-green-50 border border-green-100 text-green-700 text-sm rounded-xl px-4 py-3 mb-4">{forgotMsg}</div>
        )}
        <form onSubmit={handleForgot} className="space-y-4">
          <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required
            className="o-input py-3 max-md:text-base"
            placeholder="you@example.com" />
          <button type="submit" disabled={forgotLoading} className="o-btn o-btn-primary w-full py-3">
            {forgotLoading ? 'Sending...' : 'Send Reset Link'}
          </button>
          <button type="button" onClick={() => { setForgotMode(false); setForgotMsg(''); }}
            className="o-btn o-btn-ghost w-full">Back to login</button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 w-full max-w-md" style={{ border: '1px solid var(--line)', boxShadow: 'var(--shadow-xl)' }}>
      <div className="mb-7">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{t('Log In to Orbit')}</h2>
        <p className="text-gray-400 text-sm mt-1.5 leading-relaxed">{t('Access your secure portal to upload documents and check your application status.')}</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl px-4 py-3 mb-5">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 mb-1.5">Email address</label>
          <div className="relative">
            <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
              className="o-input pl-10 py-3 max-md:text-base"
              placeholder="you@example.com" />
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 mb-1.5">Password</label>
          <div className="relative">
            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
              className="o-input pl-10 pr-11 py-3 max-md:text-base"
              placeholder="••••••••" />
            <button type="button" onClick={() => setShowPw(!showPw)}
              className="absolute right-3.5 max-md:right-1.5 max-md:p-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
              {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer max-md:min-h-[44px]">
            <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500/20" />
            <span className="text-gray-500 text-xs">Remember me</span>
          </label>
          <button type="button" onClick={() => setForgotMode(true)} className="text-blue-600 text-xs font-medium hover:opacity-70 transition-opacity max-md:min-h-[44px] max-md:-mr-1 max-md:px-1">
            Forgot password?
          </button>
        </div>

        <button type="submit" disabled={loading} className="o-btn o-btn-primary w-full py-3">
          {loading ? t('Signing in...') : t('Log In to Orbit') + ' \u2192'}
        </button>
      </form>

      <div className="mt-5 md:mt-6 pt-5 border-t border-gray-100 text-center">
        <p className="text-gray-400 text-xs">Need help logging in?</p>
        <a href="mailto:support@capital-infusion.com" className="text-blue-600 text-xs font-medium hover:opacity-70 inline-flex items-center gap-1 mt-1 max-md:mt-0 max-md:min-h-[44px] max-md:px-2">
          Contact Support <ArrowRight size={11} />
        </a>
      </div>

      <p className="text-center mt-4 max-md:mt-1">
        <button onClick={() => setShowRegister(true)} className="text-gray-400 text-xs hover:text-gray-600 transition-colors inline-flex items-center gap-1 max-md:min-h-[44px] max-md:px-2">
          <UserPlus size={11} /> New client? Create an account
        </button>
      </p>

      {showRegister && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl p-8 w-full max-w-sm" style={{ border: '1px solid var(--line)', boxShadow: 'var(--shadow-xl)' }}>
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-11 h-11 bg-blue-50 rounded-2xl mb-4">
                <UserPlus size={20} className="text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Create Account</h2>
              <p className="text-gray-400 text-sm mt-1">Set up your client portal access</p>
            </div>
            {regError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl px-4 py-3 mb-4">
                <AlertCircle size={14} className="shrink-0" /> {regError}
              </div>
            )}
            <form onSubmit={handleRegister} className="space-y-3">
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 mb-1.5">Full Name</label>
                <input type="text" required value={regForm.full_name}
                  onChange={e => setRegForm(f => ({ ...f, full_name: e.target.value }))}
                  className="o-input"
                  placeholder="John Smith" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 mb-1.5">Business Name</label>
                <input type="text" value={regForm.business_name}
                  onChange={e => setRegForm(f => ({ ...f, business_name: e.target.value }))}
                  className="o-input"
                  placeholder="Acme LLC" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 mb-1.5">Email</label>
                <input type="email" required value={regForm.email}
                  onChange={e => setRegForm(f => ({ ...f, email: e.target.value }))}
                  className="o-input"
                  placeholder="you@business.com" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 mb-1.5">Password</label>
                <input type="password" required value={regForm.password}
                  onChange={e => setRegForm(f => ({ ...f, password: e.target.value }))}
                  className="o-input"
                  placeholder="Min 8 characters" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 mb-1.5">Confirm Password</label>
                <input type="password" required value={regForm.confirm}
                  onChange={e => setRegForm(f => ({ ...f, confirm: e.target.value }))}
                  className="o-input"
                  placeholder="••••••••" />
              </div>
              <button type="submit" disabled={regLoading} className="o-btn o-btn-primary w-full mt-1">
                {regLoading ? 'Creating account...' : 'Create Account'}
              </button>
              <button type="button" onClick={() => { setShowRegister(false); setRegError(''); }}
                className="o-btn o-btn-ghost w-full">
                Back to login
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Footer ── */
function Footer() {
  const { t } = useLanguage();
  return (
    <footer className="absolute max-md:static bottom-0 left-0 right-0 py-4 max-md:pt-0 max-md:pb-6 max-md:px-4 text-center">
      <p className="text-gray-400 text-xs">{t('Your information is 100% secure')}</p>
      <p className="text-gray-300 text-xs mt-0.5">Capital Infusion · Inc 5000 Company · Encrypted Software</p>
    </footer>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   CLIENT LOGIN PAGE — /client-login
   ══════════════════════════════════════════════════════════════════════════════ */
export default function ClientLogin() {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen relative flex flex-col">
      <Header />

      <div className="flex-1 flex flex-col lg:flex-row">
        {/* ── Left panel ── */}
        {/* pt-28 on phones clears the absolutely positioned logo header */}
        <div className="relative lg:w-[60%] flex items-center justify-center px-5 pb-7 pt-24 md:px-8 md:pb-8 md:pt-28 lg:p-16 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1e40af 100%)' }}>
          <div className="absolute inset-0 opacity-[0.06]"
            style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 800 200\'%3E%3Crect x=\'50\' y=\'60\' width=\'40\' height=\'140\' fill=\'%23fff\'/%3E%3Crect x=\'100\' y=\'30\' width=\'50\' height=\'170\' fill=\'%23fff\'/%3E%3Crect x=\'160\' y=\'80\' width=\'35\' height=\'120\' fill=\'%23fff\'/%3E%3Crect x=\'210\' y=\'20\' width=\'45\' height=\'180\' fill=\'%23fff\'/%3E%3Crect x=\'270\' y=\'50\' width=\'55\' height=\'150\' fill=\'%23fff\'/%3E%3Crect x=\'340\' y=\'10\' width=\'40\' height=\'190\' fill=\'%23fff\'/%3E%3Crect x=\'395\' y=\'70\' width=\'50\' height=\'130\' fill=\'%23fff\'/%3E%3Crect x=\'460\' y=\'40\' width=\'35\' height=\'160\' fill=\'%23fff\'/%3E%3Crect x=\'510\' y=\'25\' width=\'60\' height=\'175\' fill=\'%23fff\'/%3E%3Crect x=\'585\' y=\'55\' width=\'40\' height=\'145\' fill=\'%23fff\'/%3E%3Crect x=\'640\' y=\'35\' width=\'50\' height=\'165\' fill=\'%23fff\'/%3E%3Crect x=\'705\' y=\'65\' width=\'45\' height=\'135\' fill=\'%23fff\'/%3E%3C/svg%3E")', backgroundPosition: 'bottom', backgroundRepeat: 'repeat-x', backgroundSize: '800px 200px' }} />

          {/* Soft directional light so the panel reads as lit rather than flat */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(1000px 500px at 15% 0%, rgba(59,130,246,0.20), transparent 60%)' }}
          />

          <div className="relative z-10 max-w-lg">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight md:leading-tight mb-3 md:mb-5">{t('Welcome to Orbit')}</h1>
            <p className="text-blue-200/80 text-base md:text-lg leading-relaxed md:leading-relaxed mb-5 md:mb-10">
              {t('Your secure portal to upload documents, communicate with your advisor, and move your funding forward —')} <span className="text-green-400 font-semibold">{t('quickly and safely')}</span>.
            </p>

            <div
              className="bg-white/[0.08] backdrop-blur-md border border-white/10 rounded-2xl p-4 md:p-6 md:mb-2"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.12)' }}
            >
              <h3 className="text-white font-bold text-base md:text-lg mb-1 md:mb-1.5 tracking-tight">{t('Final Step: Upload Your Documents')}</h3>
              <p className="text-blue-200/70 text-sm leading-relaxed">
                {t("You're almost done! This is the last step in your application process. It takes about 2 minutes.")}
              </p>
            </div>

            <FeatureHighlights />
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="lg:w-[40%] flex items-center justify-center px-4 py-6 md:p-8 lg:p-12 bg-gray-50/50">
          <LoginCard />
        </div>
      </div>

      <Footer />
    </div>
  );
}
